import { useEffect, useState } from 'react';
import { useAccessToken } from '@app/auth';
import { AgentFilters, fetchAgents } from '../api/agents';
import { AgentDefinition } from '../types';

export interface UseAgentsResult {
  agents: AgentDefinition[];
  loading: boolean;
  error: string | null;
  /** True when the displayed data comes from the static fallback, not the DB. */
  usingFallback: boolean;
}

/**
 * Loads the agent list from GET /api/agents.
 *
 * Falls back to MOCK_AGENTS when:
 *  - The API returns 503 (DB unavailable — expected in local dev without a DB)
 *  - Any other network/fetch error
 *
 * This means the catalog always renders during local development, and the
 * fallback badge makes it clear that live DB data is not being used.
 */
export function useAgents(filters: AgentFilters = {}): UseAgentsResult {
  const getToken = useAccessToken();
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setUsingFallback(false);
      try {
        const dtos = await fetchAgents(getToken, filters);
        if (cancelled) return;
        // Map DB shape (AgentDto) to the local AgentDefinition type
        setAgents(
          dtos.map((d) => ({
            id: d.id,
            name: d.name,
            description: d.description ?? '',
            icon: d.icon ?? '🤖',
            color: d.color ?? '#8b949e',
            sdlcGroup: d.sdlcGroup as AgentDefinition['sdlcGroup'],
            mode: d.mode,
            capabilities: d.capabilities ?? undefined,
            tools: d.tools ?? undefined,
            toolCount: d.toolCount ?? undefined,
            externalUrl: d.externalUrl ?? undefined,
            endpoints: d.endpoints
              ? {
                  chatUrl: d.endpoints.chatUrl ?? undefined,
                  chatStreamUrl: d.endpoints.chatStreamUrl ?? undefined,
                  promptsUrl: d.endpoints.promptsUrl ?? undefined,
                  modelsUrl: d.endpoints.modelsUrl ?? undefined,
                }
              : undefined,
          }))
        );
      } catch (err: unknown) {
        if (cancelled) return;
        // DB unavailable — show empty list, no fallback data
        setAgents([]);
        setUsingFallback(true);
        setError(err instanceof Error ? err.message : 'Failed to load agents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.mode, filters.sdlcGroup]);

  return { agents, loading, error, usingFallback };
}
